import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import ProtectedRoute from '@components/ProtectedRoute/ProtectedRoute';
import DashboardLayout from '@layouts/DashboardLayout/DashboardLayout';
import StudentLayout from '@layouts/StudentLayout/StudentLayout';
import authRoutes from './auth.routes';
import mastersRoutes from './masters.routes';
import cohortRoutes from './cohort.routes';
import { studentLayoutRoutes, studentStandaloneRoutes } from './student.routes';
import adminStudentsRoutes from './adminStudents.routes';
import evaluationsRoutes from './evaluations.routes';
import settingsRoutes from './settings.routes';
import NotFoundPage from '@pages/NotFoundPage/NotFoundPage';
import LandingPage from '@pages/LandingPage/LandingPage';
import StudentApplicationsPage from '@pages/StudentApplicationsPage/StudentApplicationsPage';
import PaymentPage from '@pages/PaymentPage/PaymentPage';
import VerifyCertificatePage from '@pages/Public/VerifyCertificatePage';
/**
 * Dev-only routes. Tree-shaken out of production builds (`import.meta.env.DEV`
 * is a static `false` there, so this spreads to `[]`). Used to review the
 * Student Journey design system + shell without a student login. All lazy so
 * even the dev harness never bloats the entry chunk.
 */
const D = (f: () => Promise<{ default: React.ComponentType }>) => {
    const C = lazy(f);
    return (
        <Suspense fallback={null}>
            <C />
        </Suspense>
    );
};

const devRoutes: RouteObject[] = import.meta.env.DEV
    ? [
          { path: '/dev/journey-kit', element: D(() => import('@pages/_dev/JourneyKitPage')) },
          {
              path: '/dev/journey-shell',
              element: D(() => import('@pages/_dev/DevJourneyShell')),
              children: [
                  { index: true, element: D(() => import('@pages/StudentPortal/Journey/JourneyHome')) },
                  { path: 'week/:weekNumber', element: D(() => import('@pages/StudentPortal/Journey/WeekHub')) },
                  { path: 'learn/:weekNumber', element: D(() => import('@pages/StudentPortal/Journey/JourneyReading')) },
                  { path: 'code/:weekNumber', element: D(() => import('@pages/StudentPortal/Journey/CodingFlow')) },
                  { path: 'lab', element: D(() => import('@pages/StudentPortal/Journey/JourneyLab')) },
                  { path: 'calendar', element: D(() => import('@pages/StudentPortal/Journey/JourneyCalendar')) },
                  { path: 'achievements', element: D(() => import('@pages/StudentPortal/Journey/JourneyAchievements')) },
              ],
          },
      ]
    : [];

/**
 * Root route config.
 *
 * All protected pages (dashboard, users, roles, …) share DashboardLayout
 * which renders the Sidebar + top header.
 *
 * To add a new module: create its .routes.tsx and spread it into
 * the DashboardLayout children below.
 */
const routes: RouteObject[] = [
    ...devRoutes,
    // Landing Page
    {
        path: '/',
        element: <LandingPage />,
    },
    // Payment Page
    {
        path: '/payment/:enrollmentId',
        element: <PaymentPage />,
    },
    // Public Certificate & Project Verification Page
    {
        path: '/verify/:id',
        element: <VerifyCertificatePage />,
    },
    {
        path: '/verify',
        element: <VerifyCertificatePage />,
    },
    // Admin Redirect
    {
        path: '/admin',
        element: <Navigate to="/admin/cohorts" replace />,
    },

    // Public routes (no auth required)
    ...authRoutes,

    // Admin Protected routes → all wrapped in ProtectedRoute → DashboardLayout
    {   
        element: <ProtectedRoute />,
        children: [
            {
                element: <DashboardLayout />,
                children: [
                    ...mastersRoutes,
                    ...cohortRoutes,
                    ...adminStudentsRoutes,
                    ...evaluationsRoutes,
                    ...settingsRoutes,
                    {
                        path: '/admin/applications',
                        element: <StudentApplicationsPage />,
                    },
                ],
            },
        ],
    },

    // Student Protected Routes -> StudentLayout
    {
        element: <ProtectedRoute />,
        children: [
            ...studentStandaloneRoutes,
            {
                element: <StudentLayout />,
                children: [
                    ...studentLayoutRoutes
                ]
            }
        ]
    },

    // Unknown paths (must be last)
    { path: '*', element: <NotFoundPage /> },
];

export default routes;